import { useCallback, useMemo, useRef, useState } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import {
    Layer,
    REFERENTIAL_LAYER,
    RISK_ZONES_LAYER,
    RISK_ZONES_SOURCE,
    useMapDispatchContext,
    useMap,
    useOrderingLayers,
} from "@l2r-front/l2r-map"
import { useNetworksDispatchContext, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRiskZonesStateContext } from "../../contexts/RiskZonesContext"
import { getInterpolatedRadius, getRiskZonesLayerStyle, shiftVertically } from "../../utils/getRiskZonesConfig"

import { RiskZonesSource } from "../RiskZonesSource"

import * as Styled from "./RiskZonesLayer.styled"

const POPUP_VERTICAL_OFFSET = -10
const POPUP_DEBOUNCE_TIMEOUT = 100

export function RiskZonesLayer() {

    const { selectedLinearLocations } = useNetworksStateContext()
    const theme = useTheme()
    const { currentYear } = useRiskZonesStateContext()
    const navigateWithSearchParams = useNavigateWithSearchParams()
    const { setImageFeatureClickedPosition, setSegmentFeatureIdClicked } = useImageCollectionDispatchContext()
    const { setSelectedLinearLocations } = useNetworksDispatchContext()
    const { riskZoneId, road } = useParams()
    const { storeMapBoundingBox, setMapPin } = useMapDispatchContext()
    const { current: map } = useMap()
    const hoverTimeoutRef = useRef(null)
    const { t } = useTranslation(I18N_NAMESPACE)
    const [popupInfo, setPopupInfo] = useState(null)

    const riskZoneLayerConfig = useMemo(() => {
        const selectedRoad = selectedLinearLocations?.[0]?.road
        return getRiskZonesLayerStyle(theme, selectedRoad, currentYear, riskZoneId)
    }, [currentYear, theme, riskZoneId, selectedLinearLocations])

    useOrderingLayers([RISK_ZONES_LAYER])

    const handleRiskZonesLayerClick = useCallback((event) => {
        setMapPin()
        const map = event.target
        const roadFeatures = map.queryRenderedFeatures({
            layers: [REFERENTIAL_LAYER],
        })
        const feature = event.feature
        const roadFeatureClicked = roadFeatures.find(roadFeature => {
            const roadFeatureRoad = JSON.parse(roadFeature.properties.linearLocation)[0].road
            const roadFeatureEvent = JSON.parse(feature.properties.linearLocation).road
            return roadFeatureRoad === roadFeatureEvent
        })

        const linearLocation = JSON.parse(feature.properties.linearLocation)
        setSelectedLinearLocations(({
            ...feature,
            properties: {
                ...feature.properties,
                linearLocation: JSON.stringify(linearLocation),
            },
        }))
        setImageFeatureClickedPosition(feature.geometry.coordinates)

        setSegmentFeatureIdClicked(roadFeatureClicked?.properties?.riskZoneIdentifier)
        const newLocation = (riskZoneId || road) ? `../${linearLocation[0].road}/${feature?.properties?.riskZoneIdentifier}`
            : `${linearLocation[0].road}/${feature?.properties?.riskZoneIdentifier}`

        storeMapBoundingBox()

        return navigateWithSearchParams(newLocation)
    }, [navigateWithSearchParams, setImageFeatureClickedPosition, storeMapBoundingBox, setMapPin, riskZoneId, road, setSegmentFeatureIdClicked, setSelectedLinearLocations])

    const displayPopup = useCallback((e) => {
        if (e?.features?.[0] && map) {
            const feature = e.features[0]
            const coordinates = feature.geometry.coordinates
            const zoom = map?.getZoom()
            const radius = getInterpolatedRadius(zoom)
            const [lng, lat] = shiftVertically(map, { lng: coordinates[0], lat: coordinates[1] }, POPUP_VERTICAL_OFFSET - radius / 2)

            setPopupInfo({
                longitude: lng,
                latitude: lat,
                description: feature.properties.riskZoneIdentifier,
            })
        }
    }, [map])

    const debounceHover = useCallback((e, isEntering) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
        }

        hoverTimeoutRef.current = setTimeout(() => {
            if (isEntering) {
                displayPopup(e)
            } else {
                setPopupInfo(null)
            }
        }, POPUP_DEBOUNCE_TIMEOUT)
    }, [displayPopup])

    const onHover = useCallback((e, isEntering) => {
        debounceHover(e, isEntering)
    }, [debounceHover])

    return (
        <RiskZonesSource
            id={RISK_ZONES_SOURCE}
            tolerance={.001}
        >
            <Layer
                id={RISK_ZONES_LAYER}
                interactive
                onClick={handleRiskZonesLayerClick}
                onMouseEnter={(e) => onHover(e, true)}
                onMouseLeave={(e) => onHover(e, false)}
                {...riskZoneLayerConfig}
            />
            {
                popupInfo && (
                    <Styled.Popup
                        longitude={popupInfo.longitude}
                        latitude={popupInfo.latitude}
                        anchor="bottom"
                        closeButton={false}
                        closeOnClick={false}
                    >
                        {t(I18N_NAMESPACE, "containers.riskZonesLayer.popup", { id: popupInfo.description })}
                    </Styled.Popup>
                )
            }
        </RiskZonesSource>
    )
}

